/* AddEditImageServerComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./AddEditImageServerRenderer");
const NOT_INSTALLED_STATUS_CODE = "NOT_INSTALLED";
const MAIN_IDENTIFIER = "MAIN";

/**
 * @class AddEditImageServerComponent
 * @extends EventEmitter
 *
 * @fires AddEditImageServerComponent#Data
 * @fires AddEditImageServerComponent#Error
 * @fires AddEditImageServerComponent#Shutdown
 */

class AddEditImageServerComponent extends EventEmitter {
    constructor(data) {
        super();
        this._model = data;
        this._isMain = data.isMain;
        this._fileServerCredentialName = data.fileServerCredentialName;
        this.osImageServer = undefined;
        this._registerListeners();
        this._renderer = new Renderer(
            this,
            Object.assign({}, data.imageServer, {protocols: data.imageServerPrefs.protocols})
        );

        this.getServer(data.activeOrgId, _.get(data.imageServer, "id", null))
            .then(this._setImageServer.bind(this))
            .catch(this._triggerError.bind(this));
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer.getRootElement();
    }

    get fileServerIsNew() {
        return this.osImageServer && !this.osImageServer.existsInSystem();
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/
    _triggerData(data) {
        /**
         * @event AddEditImageServerComponent#Data
         * @param {Object} - entity properties.
         */
        this.emit("Data", data);
    }

    _triggerError(error) {
        /**
         * @event AddEditImageServerComponent#Error
         * @param {*} - entity properties.
         */
        this.emit("Error", error);
    }

    _registerListeners() {}

    _unregisterListeners() {}

    getServer(orgId, servId) {
        if (!orgId) {
            return Promise.reject({
                errorCode: "OrganizationError",
                errorMsg: "No Organization",
            });
        }
        if (servId) {
            return this._model.entityDef.loadOsImageServer(orgId, servId);
        }

        const osImageServer = new this._model.entityDef(orgId);
        osImageServer.setStatus(NOT_INSTALLED_STATUS_CODE);

        if (this._isMain) {
            osImageServer.setIsMain(true);
        }
        if (osImageServer.isMain) {
            osImageServer.setEnabled(true);
            osImageServer.setIdentifier(MAIN_IDENTIFIER);
        }
        return Promise.resolve(osImageServer);
    }

    _shutdown() {
        this.emit("Shutdown");
        this._unregisterListeners();

        return Promise.all([this._renderer.shutdown()])
            .then(() => {
                this._renderer = null;
                this._shutdownProm = null;
                this._isMain = null;
                this.osImageServer = null;
                this._fileServerCredentialName = undefined;
                this.removeAllListeners();
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */

    /*******************************************/
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    closePopUp() {
        this.rejectPopUp({userRejected: true});
    }

    resolvePopup(data) {
        this._context.resolve(data);
    }

    rejectPopUp(data) {
        this._context.reject(data);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    _setImageServer(serverEntity) {
        this.osImageServer = serverEntity;
        this._triggerData(serverEntity.getProperties());
    }
    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
    submit(data) {
        if (
            String(data.identifier).toUpperCase() === MAIN_IDENTIFIER &&
            !this.osImageServer.isMain
        ) {
            this._triggerError({
                errorCode: "MainAlreadyExists",
                errorMsg: "A Main Image Server already exists for this Organization.",
            });
            return;
        }

        this.osImageServer.setName(String(data.name).trim());
        this.osImageServer.setEnabled(Boolean(data.enabled));
        this.osImageServer.setIdentifier(String(data.identifier).trim());
        this.osImageServer.setIp(String(data.ip).trim());
        this.osImageServer.setIpv6(String(data.ipv6).trim());

        /*
        //proxyList
        this.osImageServer.setProxyList([{
            ip: String(data.proxyList[0].ip),
            userName: String(data.proxyList[0].userName),
            password: String(data.proxyList[0].password),
            // type: String(data.proxyList[0].type),
            // port: String(data.proxyList[0].port)
        }]);

         */

        const submitPromise = this.osImageServer.existsInSystem()
            ? this.osImageServer.update()
            : this.osImageServer.createStatic();

        submitPromise.then(this.closePopUp.bind(this)).catch(this._triggerError.bind(this));
    }
}

module.exports = AddEditImageServerComponent;
